import {
  Archive,
  Block,
  Call,
  ContentCopy,
  Delete,
  DoNotDisturbOn,
  EditSquare,
  Mail,
  PersonAdd,
  PhoneEnabled,
  Report,
  Security,
  Unarchive,
} from '@project-lary/react-material-symbols';
import ChannelArchiveButton from 'gcs-agent-web/src/components/ChatNavBar/ChannelArchiveButton/ChannelArchiveButton';
import { useCurrentUserToggleArchivedChannelMutation } from 'gcs-common/clients/api/entities/current-user/current-user-api';
import Anchor from 'gcs-common/design-system/ui/anchor';
import { Button, ButtonSize, ButtonVariant } from 'gcs-common/design-system/ui/button';
import { getIsHidden } from 'gcs-common/slices/channel/channelSelectors';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

interface ListItemProps {
  title: string;
  icon?: React.ReactNode;
  redFont?: boolean;
  isArchiveItem?: boolean;
  isCopyItem?: boolean;
}

// We can remove this after refactoring call melden and block refactoring
const ListItem = ({ title, icon, redFont, isArchiveItem, isCopyItem }: ListItemProps) => {
  const { routeChannelId: selectedChannelId = '' } = useParams<{ routeChannelId: string }>();

  return (
    <button className="flex items-center space-x-2 self-stretch pl-3" onClick={() => {}}>
      <div className="flex items-center justify-center self-start px-0 py-2">
        {isArchiveItem ? <ChannelArchiveButton selectedChannelId={selectedChannelId} /> : icon}
      </div>
      <div className={`flex shrink-0 grow basis-0 items-center self-stretch px-0 py-2 ${redFont ? 'text-red-11' : ''}`}>
        {title}
      </div>
      {isCopyItem && (
        <button type="button" className="flex shrink-0 basis-0 items-center justify-center self-stretch pr-8">
          <ContentCopy className="-mr-6 h-6 w-6 text-neutral-alpha-11" />
        </button>
      )}
    </button>
  );
};

export const ArchiveItem = () => {
  const { routeChannelId: selectedChannelId = '' } = useParams<{ routeChannelId: string }>();
  const isHidden = useSelector(getIsHidden(selectedChannelId));
  const toggleArchivedChannelMutation = useCurrentUserToggleArchivedChannelMutation();

  async function onChannelVisibilityButtonClick() {
    await toggleArchivedChannelMutation.mutateAsync({ channelId: selectedChannelId });
  }
  return (
    <Button
      type="button"
      className="flex w-full items-center justify-start rounded-none pl-4 hover:bg-neutral-4 active:bg-neutral-5"
      size={ButtonSize.xl}
      iconColor="text-neutral-12"
      iconSize="size-6"
      Icon={isHidden ? Unarchive : Archive}
      variant={ButtonVariant.surface_neutral}
      onClick={onChannelVisibilityButtonClick}
    >
      <div className="font-normal text-base text-neutral-12">{isHidden ? 'Archiv aufheben' : 'Chat archivieren'}</div>
    </Button>
  );
};

export const UpdateChannelName = ({ onClick }: { onClick: VoidFunction }) => {
  return (
    <Button
      type="button"
      className="flex w-full items-center justify-start rounded-none pl-4 hover:bg-neutral-4 active:bg-neutral-5"
      size={ButtonSize.xl}
      iconColor="text-neutral-12"
      iconSize="size-6"
      Icon={EditSquare}
      variant={ButtonVariant.surface_neutral}
      onClick={onClick}
    >
      <div className="font-normal text-base text-neutral-12">Chat umbenennen</div>
    </Button>
  );
};

export const AddMembersItem = ({ onClick }: { onClick: VoidFunction }) => {
  return (
    <Button
      type="button"
      className="flex w-full items-center justify-start rounded-none pl-4 hover:bg-neutral-4 active:bg-neutral-5"
      size={ButtonSize.xl}
      iconColor="text-neutral-12"
      iconSize="size-6"
      Icon={PersonAdd}
      variant={ButtonVariant.surface_neutral}
      onClick={onClick}
    >
      <div className="font-normal text-base text-neutral-12">Mitglieder hinzufügen</div>
    </Button>
  );
};

export const RemoveMemberItem = ({
  onClick,
  isLoading,
  disabled,
}: { onClick: VoidFunction; isLoading: boolean; disabled: boolean }) => {
  return (
    <Button
      type="button"
      className="flex w-full items-center justify-start rounded-none pl-4 hover:bg-neutral-4 active:bg-neutral-5"
      size={ButtonSize.xl}
      iconColor="text-error-11"
      iconSize="size-6"
      Icon={DoNotDisturbOn}
      variant={ButtonVariant.surface_neutral}
      onClick={onClick}
      disabled={isLoading || disabled}
      isLoading={isLoading}
    >
      <div className="font-normal text-base text-error-11">Aus dem Chat entfernen</div>
    </Button>
  );
};

export const ReportItem = ({ onClick }: { onClick: VoidFunction }) => {
  return (
    <Button
      type="button"
      className="flex w-full items-center justify-start rounded-none pl-4 hover:bg-neutral-4 active:bg-neutral-5"
      size={ButtonSize.xl}
      iconColor="text-error-11"
      iconSize="size-6"
      Icon={Report}
      variant={ButtonVariant.surface_neutral}
      onClick={onClick}
    >
      <div className="font-normal text-base text-error-11">Nutzer melden</div>
    </Button>
  );
};

export const BlockItem = ({ onClick }: { onClick: VoidFunction }) => {
  return (
    <Button
      type="button"
      className="flex w-full items-center justify-start rounded-none pl-4 hover:bg-neutral-4 active:bg-neutral-5"
      size={ButtonSize.xl}
      iconColor="text-error-11"
      iconSize="size-6"
      Icon={Block}
      variant={ButtonVariant.surface_neutral}
      onClick={onClick}
    >
      <div className="font-normal text-base text-error-11">Nutzer blockieren</div>
    </Button>
  );
};

export const MailItem = ({ email }: { email: string }) => {
  return (
    <Anchor
      className="flex items-start space-x-2 self-stretch py-3 pl-4 no-underline hover:bg-neutral-4 active:bg-neutral-5"
      onClick={() => {
        navigator.clipboard.writeText(email);
      }}
    >
      <Mail className="size-6 text-neutral-12" />
      <div className="w-full truncate text-base text-neutral-12 antialiased">{email}</div>
    </Anchor>
  );
};

export const PhoneItem = ({ phoneNumber }: { phoneNumber: string }) => {
  return (
    <Anchor
      className="flex items-start space-x-2 self-stretch py-3 pl-4 no-underline hover:bg-neutral-4 active:bg-neutral-5"
      onClick={() => {
        navigator.clipboard.writeText(phoneNumber);
      }}
    >
      <PhoneEnabled className="size-6 text-neutral-12" />
      <div className="w-full text-base text-neutral-12 antialiased">{phoneNumber}</div>
    </Anchor>
  );
};

// I will refactor these when we implement the new profile details page
export const CallItem = ({ phoneNumber }: { phoneNumber: string }) => (
  <ListItem title={phoneNumber} icon={<Call className="h-6 w-6 text-blue-500" />} />
);

export const NutzerMeldenItem = () => (
  <ListItem title="Nutzer melden" icon={<Security className="h-6 w-6 text-blue-500" />} redFont={true} />
);

export const NutzerBlockierenItem = () => (
  <ListItem title="Nutzer blockieren" icon={<Delete className="h-6 w-6 text-red-11" />} redFont={true} />
);
